
const BASE_URL = "/app";

const DAM_BASE_URL = `${BASE_URL}/dam`;
const COMPANY_SETTINGS_BASE_URL = `${DAM_BASE_URL}/company-settings`;

const DAM_SUBBASES = Object.freeze({
  presets: "presets",
  processPresets: "processPresets",
  analysis: "analysis",
  damAnalysis: "dam-analysis",
  processRegistration: "processRegistration",
  preprocessing: "preprocessing",
  project: "project",
  progress: "progress",
  numberRangeAssistant: "numberRangeAssistant",
  installationInstructions: "installationInstructions",
  appnamemapping: "appnamemapping",
  taskfrequencymapping: "taskfrequencymapping",
  mappingAssistant: "mappingAssistant",
  dataDeletion: "dataDeletion",
  sensorInfo: "sensorInfo",
  keywordAssistant: "keywordAssistant",
  caseIdAssistant: "caseIdAssistant",
  anonymizationAssistant: "anonymizationAssistant",
  teamSettings: "teamSettings",
  blacklisting: "blacklisting",
  whitelisting: "whitelisting",
  whitelistPhase: "whitelistPhase",
  endWhitelistPhase: "endWhitelistPhase",
  startDataCollection: "startDataCollection",
  StartTaskRegistration: "startTaskRegistration",
  endTaskRegistration: "endTaskRegistration",
  workshop: "workshop",
});

export const ANALYSIS_SUBROUTES = Object.freeze({
  dashboard: "dashboard",
  treemap: "treemap",
  automation: "automation",
  licenseChecker: "licenseChecker",
  workflow: "workflow",
  bpmnexport: "bpmnexport",
  heatmap: "heatmap",
  connections: "connections",
  shortcuts: "shortcuts",
  copyPaste: "copyPaste",
  workload: "workload",
  caseExplorer: "caseExplorer",
  dottetChart: "dottetChart",
  kpiMatrix: "kpiMatrix",

});

export const DAM_ANALYSIS_SUBROUTES = Object.freeze({
  treemap: "treemap",
  automation: "automation",
  licenseChecker: "licenseChecker",
  workflow: "workflow",
  bpmnexport: "bpmnexport",
  heatmap: "heatmap",
  connections: "connections",
  shortcuts: "shortcuts",
  copyPaste: "copyPaste",
  workload: "workload",
  caseExplorer: "caseExplorer",
  dottetChart: "dottetChart",
  kpiMatrix: "kpiMatrix",
  changeAnalysis: "changeAnalysis",

});

export const PRESETS_SUBROUTES = Object.freeze({
  anonymization: "anonymization",
  limitation: "limitation",
});

export const PROCESSREGISTRATION_SUBROUTES = Object.freeze({
  overview: "overview",
  check: "check",
  taskExplorer: "taskExplorer",
  caseIdMapper: "caseIdMapper",
  anonymizationCheck: "anonymizationCheck",
  dashboard: "dashboard",
});

export const PREPROCESSING_SUBROUTES = Object.freeze({
  appPreprocessing: "appPreprocessing",
  uiPreprocessingText: "uiPreprocessingText",
  uiPreprocessingScreen: "uiPreprocessingScreen",
  preprocessingSummary: "preprocessingSummary",
  overviewPreprocessing: "overviewPreprocessing",
  caseIdPreprocessing: "caseIdPreprocessing",
});

const KNEX_BASE_URL = `${BASE_URL}/knex`;
const MONITORING_BASE_URL = `${BASE_URL}/monitoring`;

export const KNEX_SUBROUTES = Object.freeze({
  form: "form",
  survey: "survey",
  presets: "presets",
  userList: "user-list",
  potentialScope: "potential-scope",
});

export const POTENTIAL_SCOPE_SUBROUTES = Object.freeze({
  dashboard: "dashboard",
  manageInvitations: "manageInvitations",
  taskOverview: "taskOverview",
  surveyDetail: "surveyDetail",
  suggestionBox: "suggestionBox",
});

const SETTINGS_BASE_URL = `${BASE_URL}/settings`;
const ADMIN_BASE_URL = `${BASE_URL}/admin`;

export const SETTINGS_SUBROUTES = Object.freeze({
  userManagement: "user-management",
  userHistory: "user-history",
  permissionManagement: "permission-management",
  teamManagement: "team-management",
  dataManagement: "data-management",
  processManagement: "process-management",
});

const TIMETRACKER_BASE_URL = `${BASE_URL}/tt`;
const PROCESSMODELER_BASE_URL = `${BASE_URL}/pman`;

/** 
 * @type {typeof DAM_SUBBASES} 
 */
const damSubroutes = createFullRoutes(DAM_BASE_URL, DAM_SUBBASES);

/** 
 * @type {typeof ANALYSIS_SUBROUTES} 
 */
const analysisRoutes = createFullRoutes(damSubroutes.analysis, ANALYSIS_SUBROUTES);

/** 
 * @type {typeof DAM_ANALYSIS_SUBROUTES} 
 */
const damAnalysisRoutes = createFullRoutes(damSubroutes.damAnalysis, DAM_ANALYSIS_SUBROUTES);

/**
 * @type {typeof PREPROCESSING_SUBROUTES}
 */
const preprocessingSubroutes = createFullRoutes(damSubroutes.preprocessing, PREPROCESSING_SUBROUTES);

/** 
 * @type {typeof PRESETS_SUBROUTES} 
 */
const presetsFullRoutes = createFullRoutes(damSubroutes.presets, PRESETS_SUBROUTES);

/** 
 * @type {typeof PROCESSREGISTRATION_SUBROUTES} 
 */
const processRegistrationSubroutes = createFullRoutes(damSubroutes.processRegistration, PROCESSREGISTRATION_SUBROUTES);

/**
 * @type {typeof KNEX_SUBROUTES}
 */
const knexSubroutes = createFullRoutes(KNEX_BASE_URL, KNEX_SUBROUTES);

/**
 * @type {typeof POTENTIAL_SCOPE_SUBROUTES}
 */
const potentialScopeSubroutes = createFullRoutes(knexSubroutes.potentialScope, POTENTIAL_SCOPE_SUBROUTES);

/**
 * @type {typeof SETTINGS_SUBROUTES}
 */
const settingsSubroutes = createFullRoutes(SETTINGS_BASE_URL, SETTINGS_SUBROUTES);

export const ROUTES = Object.freeze({
  home: "/",
  login: "/login",
  setup: "/setup",
  fallback: "*",
  dataDeletion: "/dataDeletion",
  userInput: "/UserInput",
  app: BASE_URL,
  frameworkAssistant: `${BASE_URL}/framework-assistant`,
  teamSurvey: `${BASE_URL}/team-survey`,
  dam: {
    root: DAM_BASE_URL,
    ...damSubroutes,
    companySettings: {
      root: COMPANY_SETTINGS_BASE_URL,
      companyInfo: `${COMPANY_SETTINGS_BASE_URL}/company-info`,
      organizationFramework: `${COMPANY_SETTINGS_BASE_URL}/organization-framework`,
      processFramework: `${COMPANY_SETTINGS_BASE_URL}/process-framework`,
      all: `${COMPANY_SETTINGS_BASE_URL}/*`,
    },
    analysis: {
      root: damSubroutes.damAnalysis,
      ...damAnalysisRoutes,
      all: `${damSubroutes.damAnalysis}/*`
    },
    presets: {
      root: damSubroutes.presets,
      ...presetsFullRoutes
    },
    all: `${DAM_BASE_URL}/*`
  },
  analysis: {
    root: damSubroutes.analysis,
    ...analysisRoutes,
    all: `${damSubroutes.analysis}/*`
  },
  processRegistration: {
    root: damSubroutes.processRegistration,
    ...processRegistrationSubroutes,
    all: `${damSubroutes.processRegistration}/*`
  },
  preprocessing: {
    root: damSubroutes.preprocessing,
    ...preprocessingSubroutes,
    all: `${damSubroutes.preprocessing}/*`
  },
  knex: {
    root: KNEX_BASE_URL,
    ...knexSubroutes,
    potentialScope: {
      root: knexSubroutes.potentialScope,
      ...potentialScopeSubroutes,
      all: `${knexSubroutes.potentialScope}/*`
    },
    all: `${KNEX_BASE_URL}/*`
  },
  monitoring: {
    root: MONITORING_BASE_URL,
    catalog: `${MONITORING_BASE_URL}/catalog`,
    licenseCatalog: `${MONITORING_BASE_URL}/license-catalog`,
    changeAnalysis: `${MONITORING_BASE_URL}/change-analysis`,
    categoryDeepDive: `${MONITORING_BASE_URL}/category-deep-dive`,
    processRoot: `${MONITORING_BASE_URL}/processes`,
    processCatalog: `${MONITORING_BASE_URL}/processes/catalog`,
    processChangeAnalysis: `${MONITORING_BASE_URL}/processes/change-analysis`,
    processDeepDive: `${MONITORING_BASE_URL}/processes/deep-dive`,
    processAll: `${MONITORING_BASE_URL}/processes/*`,
    all: `${MONITORING_BASE_URL}/*`
  },
  processModeler: PROCESSMODELER_BASE_URL,
  timeTracker: TIMETRACKER_BASE_URL,
  settings: {
    root: SETTINGS_BASE_URL,
    ...settingsSubroutes,
    all: `${SETTINGS_BASE_URL}/*`
  },
  admin: ADMIN_BASE_URL,

});


function createFullRoutes(base, subroutes) {
  const full = {};
  for (const key in subroutes) {
    full[key] = `${base}/${subroutes[key]}`;
  }
  return Object.freeze(full);
}
